import React from "react";
import Alert from "@cloudscape-design/components/alert";
import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import Container from "@cloudscape-design/components/container";
import DateRangePicker from "@cloudscape-design/components/date-range-picker";
import FormField from "@cloudscape-design/components/form-field";
import Grid from "@cloudscape-design/components/grid";
import Header from "@cloudscape-design/components/header";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import Pagination from "@cloudscape-design/components/pagination";
import SegmentedControl from "@cloudscape-design/components/segmented-control";
import Select from "@cloudscape-design/components/select";
import SpaceBetween from "@cloudscape-design/components/space-between";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Table from "@cloudscape-design/components/table";
import Tabs from "@cloudscape-design/components/tabs";
import Textarea from "@cloudscape-design/components/textarea";
import TextFilter from "@cloudscape-design/components/text-filter";
import Toggle from "@cloudscape-design/components/toggle";
import CohortConditionsEditor from "./CohortConditionsEditor";
import { copyTextToClipboard } from "../helpers/achievementTransferJson";
import { downloadTableExport } from "../helpers/tableExport";

const STATUS_OPTIONS = [
	{ value: "draft", label: "Draft" },
	{ value: "scheduled", label: "Scheduled" },
	{ value: "running", label: "Running" },
	{ value: "paused", label: "Paused" },
	{ value: "completed", label: "Completed" },
];

const STATUS_FILTER_OPTIONS = [{ value: "", label: "All statuses" }, ...STATUS_OPTIONS];

const DATE_RANGE_I18N = {
	todayAriaLabel: "Today",
	nextMonthAriaLabel: "Next month",
	previousMonthAriaLabel: "Previous month",
	customRelativeRangeDurationLabel: "Duration",
	customRelativeRangeDurationPlaceholder: "Enter duration",
	customRelativeRangeOptionLabel: "Custom range",
	customRelativeRangeOptionDescription: "Define a custom range",
	customRelativeRangeUnitLabel: "Unit of time",
	formatRelativeRange: ({ startDate, endDate }) => `${new Date(startDate).toLocaleString()} – ${new Date(endDate).toLocaleString()}`,
	formatUnit: (unit, amount) => `${unit}${amount === 1 ? "" : "s"}`,
	dateTimeConstraintText: "Select the experiment start and optional end date and time.",
	renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
	labelRelativeRange: "Relative range",
	labelAbsoluteRange: "Absolute range",
	dateTimePickerAriaLabel: "Experiment period picker",
	dateTimePickerLabel: "Date and time",
	clearButtonAriaLabel: "Clear experiment period",
	clearButtonLabel: "Clear period",
	relativeModeTitle: "Relative mode",
	absoluteModeTitle: "Absolute mode",
	cancelButtonLabel: "Cancel",
	applyButtonLabel: "Apply",
	startDateLabel: "Start date",
	startTimeLabel: "Start time",
	endDateLabel: "End date",
	endTimeLabel: "End time",
};

const FEATURE_KEY_OPTIONS = [
	{ value: "achievements", label: "achievements" },
	{ value: "registration_method", label: "registration_method" },
	{ value: "promo_guest_access", label: "promo_guest_access" },
];

const getFeatureKeyOptions = (featureKey) => (
	!featureKey || FEATURE_KEY_OPTIONS.some((option) => option.value === featureKey)
		? FEATURE_KEY_OPTIONS
		: [...FEATURE_KEY_OPTIONS, { value: featureKey, label: featureKey }]
);

const RETENTION_METRIC_KEY = "retention_day";
const DEPOSIT_NUMBER_METRIC_KEY = "deposit_number";

const BASE_METRIC_KEYS = [
	"bet_amount",
	"bets_count",
	"players_count",
	"avg_session_length",
	"avg_spins_per_session",
	"average_deposit",
	"deposit_amount",
	"deposit_conversion",
	"deposits_count",
	"ftd",
	"withdrawals_amount",
	"withdrawals_count",
	"registrations",
	"registrations_one_click",
	"registrations_email",
	"registrations_phone",
	"avg_bet",
];

const METRIC_OPTIONS = [
	...BASE_METRIC_KEYS.map((metricKey) => ({ value: metricKey, label: metricKey })),
	{ value: RETENTION_METRIC_KEY, label: "Retention day" },
	{ value: DEPOSIT_NUMBER_METRIC_KEY, label: "Deposit number" },
];

const RETENTION_ACTIVITY_OPTIONS = [
	{ value: "bet", label: "Bets" },
	{ value: "deposit", label: "Deposits" },
	{ value: "withdrawal", label: "Withdrawals" },
];

const MONEY_METRICS = new Set(["bet_amount", "avg_bet", "average_deposit", "deposit_amount", "withdrawals_amount"]);

const STATISTICS_GROUP_OPTIONS = [
	{ id: "total", text: "Total" },
	{ id: "day", text: "By day" },
	{ id: "week", text: "By week" },
];

const createStatisticsFilter = () => ({ groupBy: "total", range: null });

const addDaysToDate = (date, days) => {
	const parsed = new Date(`${date}T00:00:00Z`);
	parsed.setUTCDate(parsed.getUTCDate() + days);

	return parsed.toISOString().slice(0, 10);
};

// Statistics buckets are UTC days, so the picked dates are sent as UTC day bounds (end date inclusive).
const getStatisticsRequest = (filter, groupBy = filter.groupBy) => ({
	groupBy,
	...(filter.range?.startDate ? { dateFrom: `${filter.range.startDate.slice(0, 10)}T00:00:00Z` } : {}),
	...(filter.range?.endDate ? { dateTo: `${addDaysToDate(filter.range.endDate.slice(0, 10), 1)}T00:00:00Z` } : {}),
});

const formatRuDay = (day) => {
	const [year, month, date] = String(day || "").split("-");

	return year && month && date ? `${date}.${month}.${year}` : day || "-";
};

const formatStatisticsPeriod = (row, groupBy) => (groupBy === "week" ? `${formatRuDay(row.period)} – ${formatRuDay(addDaysToDate(row.periodEnd, -1))}` : formatRuDay(row.period));

const GROUP_LABELS = { control: "Control", variant: "Variant" };

// Breakdown rows are metrics (one per group); every period becomes a column.
const buildBreakdownItems = (breakdown) => (breakdown?.metrics || []).flatMap((metricKey) => ["control", "variant"].map((groupKey) => ({ rowKey: `${metricKey}-${groupKey}`, metricKey, groupKey })));

const getExportMetricValue = (metricKey, value, isRetention) => {
	const number = isRetention ? Number(value) : getMetricNumber(metricKey, value);

	return value === null || typeof value === "undefined" || !Number.isFinite(number) ? "" : number;
};

const getExperimentJson = (payload) => ({
	...payload,
	metrics: (payload.metrics || []).map(({ id, ...metric }) => metric),
});
const SECONDS_METRICS = new Set(["avg_session_length"]);

const createMetricRowKey = () => `metric-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const getRetentionMetricLabel = (metric) => {
	const day = Number(metric.retentionDay);

	return Number.isInteger(day) && day >= 1 ? `D${day}` : "Retention day";
};

const getRetentionActivityLabel = (activityType) => RETENTION_ACTIVITY_OPTIONS.find((option) => option.value === activityType)?.label || "";

const getRetentionActivityError = (activityType) => {
	if (!activityType) return "Activity type is required";
	if (!RETENTION_ACTIVITY_OPTIONS.some((option) => option.value === activityType)) return "Select a supported activity type";

	return "";
};

const getRetentionDayError = (value) => {
	const retentionDayText = String(value ?? "").trim();
	const retentionDay = Number(retentionDayText);
	if (!retentionDayText) return "Retention day is required";
	if (!Number.isInteger(retentionDay)) return "Retention day must be a whole number";
	if (retentionDay < 1) return "Retention day must be at least 1";

	return "";
};

const parseDepositNumbers = (value) => {
	const text = String(value ?? "").trim();
	if (!text) return { error: "At least one deposit number is required" };

	const parts = text.split(",").map((part) => part.trim());
	if (parts.some((part) => !part)) return { error: "Enter deposit numbers separated by commas, for example 1, 2" };

	const numbers = parts.map(Number);
	if (numbers.some((number) => !Number.isInteger(number))) return { error: "Deposit numbers must be whole numbers" };
	if (numbers.some((number) => number < 1)) return { error: "Deposit numbers must be at least 1" };
	if (new Set(numbers).size !== numbers.length) return { error: "Deposit numbers must not repeat" };

	return { value: numbers };
};

const getDepositNumberMetricLabel = (metric) => {
	const parsed = parseDepositNumbers(metric.depositNumbersText);

	return parsed.error ? "Deposit number" : parsed.value.map((number) => `DEP${number}`).join(", ");
};

const createDefaultForm = () => ({
	id: null,
	code: "",
	name: "",
	featureKey: "",
	status: "draft",
	startsDate: "",
	startsTime: "00:00",
	endsDate: "",
	endsTime: "00:00",
	cohortConditions: [],
	control: { name: "Control", description: "", enabled: true, payloadText: "{}" },
	variant: { name: "Variant", description: "", enabled: true, payloadText: "{}", allocationPercent: 50 },
	metrics: [],
	metricToAdd: null,
	editorKey: Date.now(),
});

const getErrorText = (error) => {
	if (typeof error?.text === "string") return error.text;
	if (typeof error?.message === "string") return error.message;
	if (typeof error === "string") return error;
	if (error?.text) {
		try {
			return JSON.stringify(error.text);
		} catch (jsonError) {
			return String(error.text);
		}
	}

	return "Request failed";
};

const pad = (value) => String(value).padStart(2, "0");

const splitDateTime = (value) => {
	if (!value) return { date: "", time: "00:00" };
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return { date: "", time: "00:00" };

	return {
		date: `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`,
		time: `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`,
	};
};

const combineDateTime = (date, time) => {
	if (!date) return null;
	const parsed = new Date(`${date}T${time || "00:00"}:00`);

	return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const toPickerDateTime = (date, time) => (date ? `${date}T${time || "00:00"}:00` : "");

const splitPickerDateTime = (value) => {
	if (!value) return { date: "", time: "00:00" };
	const [date, rawTime = "00:00"] = value.split("T");

	return { date, time: rawTime.slice(0, 5) || "00:00" };
};

const parseJsonObject = (value, label) => {
	try {
		const parsed = JSON.parse(value || "{}");
		if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
			return { error: `${label} must be a JSON object` };
		}

		return { value: parsed };
	} catch (error) {
		return { error: `${label}: ${error.message}` };
	}
};

const getJsonObjectError = (value, label) => parseJsonObject(value, label).error || "";

const getMetricSettingsError = (metric) => (
	getJsonObjectError(metric.settingsText, `${metric.metricKey} settings`)
);

const formatDateTime = (value) => {
	if (!value) return "-";
	const date = new Date(value);

	return Number.isNaN(date.getTime()) ? value : `${date.toLocaleDateString("ru-RU")} ${date.toLocaleTimeString("ru-RU")}`;
};

const formatNumber = (value) => {
	if (value === null || typeof value === "undefined") return "-";
	const number = Number(value);

	return Number.isFinite(number) ? new Intl.NumberFormat().format(number) : String(value);
};

const getMetricNumber = (metricKey, value, emptyAsZero = false) => {
	if (value === null || typeof value === "undefined") return emptyAsZero ? 0 : null;
	const number = Number(value);
	if (!Number.isFinite(number)) return null;

	return MONEY_METRICS.has(metricKey) ? number / 100 : number;
};

const formatMetricNumber = (metricKey, value) => {
	if (value === null || typeof value === "undefined" || !Number.isFinite(Number(value))) return "-";
	const formatted = new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 0,
		maximumFractionDigits: MONEY_METRICS.has(metricKey) ? 2 : 3,
	}).format(Number(value));

	if (MONEY_METRICS.has(metricKey)) return `${formatted} RUB.`;
	if (SECONDS_METRICS.has(metricKey)) return `${formatted} sec.`;

	return formatted;
};

const formatMetricValue = (metricKey, value) => formatMetricNumber(metricKey, getMetricNumber(metricKey, value));

const isRetentionStatisticsMetric = (metricKey, activityType) => Boolean(activityType) || /^D\d+$/.test(metricKey || "");

const getStatisticsMetricNumber = (metric, value, emptyAsZero = false) => {
	if (!metric.isRetention) return getMetricNumber(metric.metricKey, value, emptyAsZero);
	if (value === null || typeof value === "undefined") return emptyAsZero ? 0 : null;
	const number = Number(value);

	return Number.isFinite(number) ? number : null;
};

const formatRetentionPercentage = (value) => {
	if (value === null || typeof value === "undefined" || !Number.isFinite(Number(value))) return "-";

	return `${new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(Number(value))}%`;
};

const formatStatisticsMetricValue = (metric, value) => (
	metric.isRetention ? formatRetentionPercentage(value) : formatMetricValue(metric.metricKey, value)
);

const formatStatisticsMetricNumber = (metric, value) => (
	metric.isRetention ? formatRetentionPercentage(value) : formatMetricNumber(metric.metricKey, value)
);

const buildStatisticsRows = (statistics) => {
	if (!statistics) return [];
	const controlValues = statistics.groups?.control?.values || {};
	const variantValues = statistics.groups?.variant?.values || {};
	const controlRetention = statistics.groups?.control?.retention || {};
	const variantRetention = statistics.groups?.variant?.retention || {};
	const statisticMetrics = Array.isArray(statistics.metrics) ? statistics.metrics : [];
	const rows = statisticMetrics.map((metric, index) => {
		const isRetention = isRetentionStatisticsMetric(metric.metricKey, metric.activityType);

		return {
			rowKey: `statistics-${metric.metricKey}-${metric.activityType || "base"}-${metric.retentionDay ?? "base"}-${index}`,
			metricKey: metric.metricKey,
			activityType: metric.activityType || null,
			isRetention,
			control: controlValues[metric.metricKey] ?? (isRetention ? controlRetention[metric.metricKey]?.value : undefined),
			variant: variantValues[metric.metricKey] ?? (isRetention ? variantRetention[metric.metricKey]?.value : undefined),
			winner: metric.winner ?? null,
		};
	});
	const representedMetricKeys = new Set(statisticMetrics.map((metric) => metric.metricKey));
	const extraMetricKeys = Array.from(new Set([...Object.keys(controlValues), ...Object.keys(variantValues)])).filter((metricKey) => !representedMetricKeys.has(metricKey));

	return [
		...rows,
		...extraMetricKeys.map((metricKey) => ({
			rowKey: `statistics-value-${metricKey}`,
			metricKey,
			activityType: null,
			isRetention: isRetentionStatisticsMetric(metricKey),
			control: controlValues[metricKey],
			variant: variantValues[metricKey],
			winner: null,
		})),
	];
};

const statusType = (status) => {
	if (status === "running") return "success";
	if (status === "scheduled") return "pending";
	if (status === "paused") return "warning";
	if (status === "completed") return "stopped";

	return "info";
};

const winnerType = (winner) => {
	if (winner === "variant") return "success";
	if (winner === "control") return "info";
	if (winner === "tie") return "stopped";

	return "pending";
};

const normalizeExperiment = (rawExperiment) => {
	const experiment = rawExperiment?.experiment
		? { ...rawExperiment.experiment, metrics: rawExperiment.metrics || rawExperiment.experiment.metrics || [] }
		: rawExperiment;
	const startsAt = splitDateTime(experiment?.startsAt);
	const endsAt = splitDateTime(experiment?.endsAt);
	const variantAllocation = Number(experiment?.variant?.allocationPercent);
	const controlAllocation = Number(experiment?.control?.allocationPercent);

	return {
		...createDefaultForm(),
		id: experiment?.id || null,
		code: experiment?.code || "",
		name: experiment?.name || "",
		featureKey: experiment?.featureKey || "",
		status: experiment?.status || "draft",
		startsDate: startsAt.date,
		startsTime: startsAt.time,
		endsDate: endsAt.date,
		endsTime: endsAt.time,
		cohortConditions: Array.isArray(experiment?.cohortConditions) ? experiment.cohortConditions : [],
		control: {
			name: experiment?.control?.name || "Control",
			description: experiment?.control?.description || "",
			enabled: experiment?.control?.enabled !== false,
			payloadText: JSON.stringify(experiment?.control?.payload || {}, null, 2),
		},
		variant: {
			name: experiment?.variant?.name || "Variant",
			description: experiment?.variant?.description || "",
			enabled: experiment?.variant?.enabled !== false,
			payloadText: JSON.stringify(experiment?.variant?.payload || {}, null, 2),
			allocationPercent: Number.isFinite(variantAllocation) ? variantAllocation : Number.isFinite(controlAllocation) ? 100 - controlAllocation : 50,
		},
		metrics: (experiment?.metrics || []).map((metric, index) => ({
			id: metric.id,
			rowKey: metric.id ? `metric-${metric.id}` : `metric-${metric.metricKey}-${index}`,
			metricKey: metric.metricKey,
			settingsText: [RETENTION_METRIC_KEY, DEPOSIT_NUMBER_METRIC_KEY].includes(metric.metricKey) ? "{}" : JSON.stringify(metric.settings || {}, null, 2),
			activityType: metric.metricKey === RETENTION_METRIC_KEY ? metric.settings?.activityType || "" : "",
			retentionDay: metric.metricKey === RETENTION_METRIC_KEY && metric.settings?.days?.[0] !== null && typeof metric.settings?.days?.[0] !== "undefined" ? String(metric.settings.days[0]) : "",
			depositNumbersText: metric.metricKey === DEPOSIT_NUMBER_METRIC_KEY && Array.isArray(metric.settings?.numbers) ? metric.settings.numbers.join(", ") : "",
			enabled: metric.enabled !== false,
		})),
		metricToAdd: null,
		editorKey: Date.now(),
	};
};

const buildExperimentPayload = (form) => {
	const errors = {};
	if (!form.code.trim()) errors.code = "Code is required";
	if (!form.name.trim()) errors.name = "Name is required";
	if (!form.featureKey.trim()) errors.featureKey = "Feature key is required";
	if (!form.control.name.trim()) errors.controlName = "Control name is required";
	if (!form.variant.name.trim()) errors.variantName = "Variant name is required";

	const controlPayload = parseJsonObject(form.control.payloadText, "Control payload");
	const variantPayload = parseJsonObject(form.variant.payloadText, "Variant payload");
	if (controlPayload.error) errors.controlPayload = controlPayload.error;
	if (variantPayload.error) errors.variantPayload = variantPayload.error;

	const allocationPercent = Number(form.variant.allocationPercent);
	if (!Number.isFinite(allocationPercent) || allocationPercent < 0 || allocationPercent > 100) {
		errors.allocationPercent = "Allocation must be between 0 and 100";
	}

	const startsAt = combineDateTime(form.startsDate, form.startsTime);
	const endsAt = combineDateTime(form.endsDate, form.endsTime);
	if (["scheduled", "running"].includes(form.status) && !startsAt) errors.startsAt = "Start date is required for scheduled and running experiments";
	if (form.startsDate && !startsAt) errors.startsAt = "Invalid start date or time";
	if (form.endsDate && !endsAt) errors.endsAt = "Invalid end date or time";
	if (form.status === "running" && startsAt && new Date(startsAt).getTime() > Date.now()) errors.startsAt = "A running experiment cannot start in the future";
	if (form.status === "running" && endsAt && new Date(endsAt).getTime() <= Date.now()) errors.endsAt = "A running experiment must end in the future";
	if (startsAt && endsAt && new Date(endsAt).getTime() <= new Date(startsAt).getTime()) errors.endsAt = "End date must be later than start date";

	const metrics = form.metrics.map((metric, index) => {
		let settings;
		if (metric.metricKey === RETENTION_METRIC_KEY) {
			const retentionDayText = String(metric.retentionDay ?? "").trim();
			const retentionDay = Number(retentionDayText);
			const activityError = getRetentionActivityError(metric.activityType);
			const dayError = getRetentionDayError(metric.retentionDay);
			if (activityError) errors[`metric-${index}-activity`] = activityError;
			if (dayError) errors[`metric-${index}-day`] = dayError;

			settings = {
				activityType: metric.activityType || "",
				days: [retentionDay],
			};
		} else if (metric.metricKey === DEPOSIT_NUMBER_METRIC_KEY) {
			const parsedNumbers = parseDepositNumbers(metric.depositNumbersText);
			if (parsedNumbers.error) errors[`metric-${index}-deposit-numbers`] = parsedNumbers.error;

			settings = {
				numbers: parsedNumbers.value || [],
			};
		} else {
			const parsedSettings = parseJsonObject(metric.settingsText, `${metric.metricKey} settings`);
			if (parsedSettings.error) errors[`metric-${index}-settings`] = parsedSettings.error;
			settings = parsedSettings.value || {};
		}

		return {
			...(metric.id ? { id: metric.id } : {}),
			metricKey: metric.metricKey,
			settings,
			enabled: metric.enabled,
		};
	});

	return {
		errors,
		payload: {
			code: form.code.trim(),
			name: form.name.trim(),
			featureKey: form.featureKey.trim(),
			status: form.status,
			startsAt,
			endsAt,
			cohortConditions: form.cohortConditions || [],
			control: {
				name: form.control.name.trim(),
				description: form.control.description,
				enabled: form.control.enabled,
				payload: controlPayload.value || {},
			},
			variant: {
				name: form.variant.name.trim(),
				description: form.variant.description,
				enabled: form.variant.enabled,
				payload: variantPayload.value || {},
				allocationPercent,
			},
			metrics,
		},
	};
};

const buildEditPayload = (id, payload, originalPayload) => {
	const editPayload = { id };
	const isDifferent = (left, right) => JSON.stringify(left) !== JSON.stringify(right);

	["code", "name", "featureKey", "status", "startsAt", "endsAt", "cohortConditions", "metrics"].forEach((field) => {
		if (isDifferent(payload[field], originalPayload?.[field])) editPayload[field] = payload[field];
	});

	["control", "variant"].forEach((group) => {
		const changes = {};
		Object.keys(payload[group]).forEach((field) => {
			if (isDifferent(payload[group][field], originalPayload?.[group]?.[field])) changes[field] = payload[group][field];
		});
		if (Object.keys(changes).length) editPayload[group] = changes;
	});

	return editPayload;
};

function ABTests({ Interface }) {
	const [items, setItems] = React.useState([]);
	const [loading, setLoading] = React.useState(true);
	const [pageError, setPageError] = React.useState("");
	const [search, setSearch] = React.useState("");
	const [appliedSearch, setAppliedSearch] = React.useState("");
	const [status, setStatus] = React.useState("");
	const [sorting, setSorting] = React.useState({ column: "id", order: "DESC" });
	const [pagination, setPagination] = React.useState({ page: 1, limit: 15, total: 0, pages: 1 });
	const [formModal, setFormModal] = React.useState({ visible: false, mode: "create", loading: false, saving: false });
	const [form, setForm] = React.useState(createDefaultForm);
	const [originalPayload, setOriginalPayload] = React.useState(null);
	const [formError, setFormError] = React.useState("");
	const [fieldErrors, setFieldErrors] = React.useState({});
	const [activeFormTab, setActiveFormTab] = React.useState("general");
	const [deleteModal, setDeleteModal] = React.useState({ visible: false, item: null, loading: false, error: "" });
	const [statisticsModal, setStatisticsModal] = React.useState({ visible: false, item: null, loading: false, data: null, error: "" });
	const [statisticsFilter, setStatisticsFilter] = React.useState(createStatisticsFilter);
	const [statisticsExporting, setStatisticsExporting] = React.useState(false);
	const [jsonText, setJsonText] = React.useState("");
	const [jsonError, setJsonError] = React.useState("");

	const loadList = React.useCallback(async () => {
		setLoading(true);
		setPageError("");
		const data = {
			page: pagination.page,
			limit: pagination.limit,
			search: appliedSearch,
			sorting,
		};
		if (status) data.status = status;

		try {
			const response = await Interface.request("get", { class: "data", module: "abTest", method: "list" }, data);
			setItems(Array.isArray(response?.items) ? response.items : []);
			setPagination((current) => ({
				...current,
				page: Number(response?.pagination?.page) || current.page,
				limit: Number(response?.pagination?.limit) || current.limit,
				total: Number(response?.pagination?.total) || 0,
				pages: Number(response?.pagination?.pages) || 1,
			}));
		} catch (error) {
			setItems([]);
			setPageError(getErrorText(error));
		} finally {
			setLoading(false);
		}
	}, [Interface, appliedSearch, pagination.limit, pagination.page, sorting, status]);

	React.useEffect(() => {
		loadList();
	}, [loadList]);

	const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));
	const updateGroup = (group, patch) => setForm((current) => ({ ...current, [group]: { ...current[group], ...patch } }));

	const openCreate = () => {
		setForm(createDefaultForm());
		setOriginalPayload(null);
		setFieldErrors({});
		setFormError("");
		setActiveFormTab("general");
		setFormModal({ visible: true, mode: "create", loading: false, saving: false });
	};

	const openEdit = async (item) => {
		setForm(createDefaultForm());
		setOriginalPayload(null);
		setFieldErrors({});
		setFormError("");
		setActiveFormTab("general");
		setFormModal({ visible: true, mode: "edit", loading: true, saving: false });

		try {
			const response = await Interface.request("get", { class: "data", module: "abTest", method: "get" }, { id: item.id });
			const nextForm = normalizeExperiment(response);
			const normalized = buildExperimentPayload(nextForm);
			setForm(nextForm);
			setOriginalPayload(normalized.payload);
			setFormModal({ visible: true, mode: "edit", loading: false, saving: false });
		} catch (error) {
			setFormError(getErrorText(error));
			setFormModal({ visible: true, mode: "edit", loading: false, saving: false });
		}
	};

	const closeForm = () => {
		if (formModal.saving) return;
		setFormModal({ visible: false, mode: "create", loading: false, saving: false });
		setFormError("");
		setFieldErrors({});
	};

	const saveExperiment = async () => {
		const normalized = buildExperimentPayload(form);
		setFieldErrors(normalized.errors);
		setFormError("");
		if (Object.keys(normalized.errors).length) {
			const errorKeys = Object.keys(normalized.errors);
			if (errorKeys.some((key) => ["code", "name", "featureKey", "startsAt", "endsAt"].includes(key))) setActiveFormTab("general");
			else if (errorKeys.some((key) => key.startsWith("metric-"))) setActiveFormTab("metrics");
			else setActiveFormTab("variants");
			return;
		}

		const isEdit = formModal.mode === "edit";
		const payload = isEdit ? buildEditPayload(form.id, normalized.payload, originalPayload) : normalized.payload;
		if (isEdit && Object.keys(payload).length === 1) {
			Interface.addNoty("Success", "No A/B test changes to save");
			closeForm();
			return;
		}

		setFormModal((current) => ({ ...current, saving: true }));
		try {
			await Interface.request("set", { class: "data", module: "abTest", method: isEdit ? "edit" : "create" }, payload);
			Interface.addNoty("Success", `A/B test ${isEdit ? "updated" : "created"}`);
			setFormModal({ visible: false, mode: "create", loading: false, saving: false });
			await loadList();
		} catch (error) {
			setFormError(getErrorText(error));
			setFormModal((current) => ({ ...current, saving: false }));
		}
	};

	const addMetric = () => {
		const metricKey = form.metricToAdd?.value;
		if (!metricKey || (metricKey !== RETENTION_METRIC_KEY && form.metrics.some((metric) => metric.metricKey === metricKey))) return;
		updateForm({
			metrics: [
				...form.metrics,
				{
					rowKey: createMetricRowKey(),
					metricKey,
					settingsText: "{}",
					activityType: "",
					retentionDay: "",
					depositNumbersText: "",
					enabled: true,
				},
			],
			metricToAdd: null,
		});
	};

	const updateMetric = (rowKey, patch) => {
		setForm((current) => ({
			...current,
			metrics: current.metrics.map((metric) => (metric.rowKey === rowKey ? { ...metric, ...patch } : metric)),
		}));
	};

	const changeStatus = async (item, nextStatus) => {
		setPageError("");
		const payload = { id: item.id, status: nextStatus };
		if (nextStatus === "running" && (!item.startsAt || new Date(item.startsAt).getTime() > Date.now())) payload.startsAt = new Date().toISOString();

		try {
			await Interface.request("set", { class: "data", module: "abTest", method: "edit" }, payload);
			Interface.addNoty("Success", `A/B test status changed to ${nextStatus}`);
			await loadList();
		} catch (error) {
			setPageError(getErrorText(error));
		}
	};

	const confirmDelete = async () => {
		if (!deleteModal.item || deleteModal.item.status !== "draft") return;
		setDeleteModal((current) => ({ ...current, loading: true, error: "" }));

		try {
			await Interface.request("set", { class: "data", module: "abTest", method: "delete" }, { id: deleteModal.item.id });
			Interface.addNoty("Success", "A/B test deleted");
			setDeleteModal({ visible: false, item: null, loading: false, error: "" });
			await loadList();
		} catch (error) {
			setDeleteModal((current) => ({ ...current, loading: false, error: getErrorText(error) }));
		}
	};

	const loadStatistics = async (item, filter) => {
		setStatisticsFilter(filter);
		setStatisticsModal((current) => ({ visible: true, item, loading: true, data: current.item?.id === item.id ? current.data : null, error: "" }));
		try {
			const data = await Interface.request("get", { class: "data", module: "abTest", method: "statistics" }, { id: item.id, ...getStatisticsRequest(filter) });
			setStatisticsModal({ visible: true, item, loading: false, data, error: "" });
		} catch (error) {
			setStatisticsModal({ visible: true, item, loading: false, data: null, error: getErrorText(error) });
		}
	};

	const openStatistics = (item) => {
		loadStatistics(item, createStatisticsFilter());
	};

	const closeStatistics = () => {
		setStatisticsModal({ visible: false, item: null, loading: false, data: null, error: "" });
		setStatisticsFilter(createStatisticsFilter());
	};

	// Exports every day of the selected period plus a total row with all configured metrics.
	const exportStatistics = async () => {
		const item = statisticsModal.item;
		if (!item) return;

		setStatisticsExporting(true);
		try {
			const data = await Interface.request("get", { class: "data", module: "abTest", method: "statistics" }, { id: item.id, ...getStatisticsRequest(statisticsFilter, "day") });
			const metrics = Array.isArray(data?.metrics) ? data.metrics : [];
			const dailyMetricKeys = new Set(data?.breakdown?.metrics || []);
			// Newest day first, the same as the table.
			const days = [...(data?.breakdown?.rows || [])].reverse();
			const columns = [
				{ value: "metric", label: "Metric" },
				{ value: "group", label: "Group" },
				{ value: "total", label: "Total" },
				...days.map((day) => ({ value: day.period, label: formatRuDay(day.period) })),
			];
			const rows = [];
			metrics.forEach((metric) => {
				const isRetention = isRetentionStatisticsMetric(metric.metricKey, metric.activityType);
				const isDaily = dailyMetricKeys.has(metric.metricKey);
				["control", "variant"].forEach((groupKey) => {
					rows.push({
						metric: `${metric.metricKey}${MONEY_METRICS.has(metric.metricKey) ? " (RUB)" : ""}${isRetention ? " (%)" : ""}`,
						group: GROUP_LABELS[groupKey],
						total: getExportMetricValue(metric.metricKey, data?.groups?.[groupKey]?.values?.[metric.metricKey], isRetention),
						...Object.fromEntries(days.map((day) => [day.period, isDaily ? getExportMetricValue(metric.metricKey, day[groupKey]?.values?.[metric.metricKey], false) : ""])),
					});
				});
			});

			downloadTableExport({ rows, columns, format: "csv", filename: `ab-test-${item.code || item.id}-statistics.csv` });
		} catch (error) {
			Interface.addNoty("Error", getErrorText(error) || "Couldn't export statistics");
		} finally {
			setStatisticsExporting(false);
		}
	};

	const getFormJsonText = () => JSON.stringify(getExperimentJson(buildExperimentPayload(form).payload), null, 2);

	const copyExperimentJson = async () => {
		const { errors, payload } = buildExperimentPayload(form);
		if (Object.keys(errors).length) {
			setJsonError(`Fix the form before copying: ${Object.values(errors).join(", ")}`);
			return;
		}

		const value = JSON.stringify(getExperimentJson(payload), null, 2);
		setJsonText(value);
		setJsonError("");
		try {
			await copyTextToClipboard(value);
			Interface.addNoty("Success", "A/B test JSON copied");
		} catch (error) {
			Interface.addNoty("Error", "Failed to copy A/B test JSON");
		}
	};

	const applyExperimentJson = () => {
		try {
			const parsed = JSON.parse(jsonText);
			if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("JSON must be an object");

			setForm((current) => ({ ...normalizeExperiment(parsed), id: current.id, editorKey: Date.now() }));
			setFieldErrors({});
			setJsonError("");
			Interface.addNoty("Success", "JSON applied to A/B test form");
		} catch (error) {
			setJsonError(error.message ? `Invalid JSON: ${error.message}` : "Invalid JSON");
		}
	};

	React.useEffect(() => {
		if (formModal.visible && activeFormTab === "json") {
			setJsonText(getFormJsonText());
			setJsonError("");
		}
	}, [formModal.visible, activeFormTab]);

	const renderStatusActions = (item) => {
		if (item.status === "draft") return <><Button disabled={!item.startsAt} onClick={() => changeStatus(item, "scheduled")}>Schedule</Button><Button onClick={() => changeStatus(item, "running")}>Start</Button></>;
		if (item.status === "scheduled") return <><Button onClick={() => changeStatus(item, "running")}>Start now</Button><Button onClick={() => changeStatus(item, "draft")}>Draft</Button></>;
		if (item.status === "running") return <><Button onClick={() => changeStatus(item, "paused")}>Pause</Button><Button onClick={() => changeStatus(item, "completed")}>Complete</Button></>;
		if (item.status === "paused") return <><Button onClick={() => changeStatus(item, "running")}>Resume</Button><Button onClick={() => changeStatus(item, "completed")}>Complete</Button></>;

		return null;
	};

	const statisticsRows = React.useMemo(() => buildStatisticsRows(statisticsModal.data), [statisticsModal.data]);
	const breakdownItems = React.useMemo(() => buildBreakdownItems(statisticsModal.data?.breakdown), [statisticsModal.data]);

	const availableMetricOptions = METRIC_OPTIONS.filter((option) => option.value === RETENTION_METRIC_KEY || !form.metrics.some((metric) => metric.metricKey === option.value));
	const variantAllocation = Math.min(100, Math.max(0, Number(form.variant.allocationPercent) || 0));

	return (
		<SpaceBetween size="m">
			{pageError ? <Alert type="error" dismissible onDismiss={() => setPageError("")} header="A/B test request failed">{pageError}</Alert> : null}
			<Table
				stickyHeader
				trackBy="id"
				loading={loading}
				items={items}
				sortingColumn={{ sortingField: sorting.column }}
				sortingDescending={sorting.order === "DESC"}
				onSortingChange={({ detail }) => {
					setSorting({ column: detail.sortingColumn.sortingField, order: detail.isDescending ? "DESC" : "ASC" });
					setPagination((current) => ({ ...current, page: 1 }));
				}}
				columnDefinitions={[
					{
						id: "id",
						header: "ID",
						sortingField: "id",
						cell: (item) => (
							<Button iconName="status-in-progress" variant="normal" onClick={() => openEdit(item)}>
								{item.id}
							</Button>
						),
						minWidth: 120,
					},
					{ id: "code", header: "Code", sortingField: "code", cell: (item) => item.code || "-" },
					{ id: "name", header: "Name", sortingField: "name", cell: (item) => item.name || "-" },
					{ id: "featureKey", header: "Feature key", sortingField: "featureKey", cell: (item) => item.featureKey || "-" },
					{ id: "status", header: "Status", sortingField: "status", cell: (item) => <StatusIndicator type={statusType(item.status)}>{item.status || "draft"}</StatusIndicator> },
					{ id: "startsAt", header: "Starts", sortingField: "startsAt", cell: (item) => formatDateTime(item.startsAt) },
					{ id: "endsAt", header: "Ends", sortingField: "endsAt", cell: (item) => formatDateTime(item.endsAt) },
					{ id: "allocation", header: "Allocation", cell: (item) => `${item.control?.allocationPercent ?? 100 - Number(item.variant?.allocationPercent || 0)}% / ${item.variant?.allocationPercent ?? 0}%` },
					{ id: "metricsCount", header: "Metrics", cell: (item) => item.metricsCount ?? 0 },
					{
						id: "actions",
						header: "Actions",
						cell: (item) => (
							<SpaceBetween direction="horizontal" size="xs">
								<Button onClick={() => openStatistics(item)}>Statistics</Button>
								{renderStatusActions(item)}
								{item.status === "draft" ? <Button onClick={() => setDeleteModal({ visible: true, item, loading: false, error: "" })}>Delete</Button> : null}
							</SpaceBetween>
						),
						minWidth: 360,
					},
				]}
				filter={
					<SpaceBetween direction="horizontal" size="xs">
						<TextFilter
							filteringText={search}
							filteringPlaceholder="Search A/B tests"
							onChange={({ detail }) => {
								setSearch(detail.filteringText);
								if (!detail.filteringText) {
									setAppliedSearch("");
									setPagination((current) => ({ ...current, page: 1 }));
								}
							}}
						/>
						<Button onClick={() => { setAppliedSearch(search.trim()); setPagination((current) => ({ ...current, page: 1 })); }}>Search</Button>
						<Select
							selectedOption={STATUS_FILTER_OPTIONS.find((option) => option.value === status) || STATUS_FILTER_OPTIONS[0]}
							options={STATUS_FILTER_OPTIONS}
							onChange={({ detail }) => { setStatus(detail.selectedOption.value); setPagination((current) => ({ ...current, page: 1 })); }}
						/>
					</SpaceBetween>
				}
				pagination={<Pagination currentPageIndex={pagination.page} pagesCount={pagination.pages} onChange={({ detail }) => setPagination((current) => ({ ...current, page: detail.currentPageIndex }))} />}
				header={<Header counter={`(${pagination.total})`} description="Create experiments, control rollout, and compare configured metrics." actions={<Button variant="primary" onClick={openCreate}>Create A/B test</Button>}>A/B Tests</Header>}
				empty={<Box textAlign="center">No A/B tests found</Box>}
			/>

			<Modal
				visible={formModal.visible}
				onDismiss={closeForm}
				size="max"
				header={formModal.mode === "edit" ? `Edit A/B test${form.id ? ` #${form.id}` : ""}` : "Create A/B test"}
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button onClick={closeForm} disabled={formModal.saving}>Cancel</Button>
							<Button variant="primary" loading={formModal.saving} disabled={formModal.loading || (formModal.mode === "edit" && !form.id)} onClick={saveExperiment}>Save</Button>
						</SpaceBetween>
					</Box>
				}
			>
				{formModal.loading ? (
					<Box textAlign="center" padding="xxl">Loading A/B test…</Box>
				) : (
					<SpaceBetween size="l">
						{formError ? <Alert type="error">{formError}</Alert> : null}
						<Tabs
							activeTabId={activeFormTab}
							onChange={({ detail }) => setActiveFormTab(detail.activeTabId)}
							tabs={[
								{
									id: "general",
									label: "General",
									content: (
						<Container header={<Header variant="h2">Experiment</Header>}>
							<SpaceBetween size="m">
								<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
									<FormField label="Experiment code" description="Unique technical identifier used by the backend." errorText={fieldErrors.code} stretch>
										<Input value={form.code} onChange={({ detail }) => updateForm({ code: detail.value })} placeholder="checkout_v1" />
									</FormField>
									<FormField label="Experiment name" description="Human-readable name shown in the administration panel." errorText={fieldErrors.name} stretch>
										<Input value={form.name} onChange={({ detail }) => updateForm({ name: detail.value })} placeholder="Checkout experiment" />
									</FormField>
									<FormField label="Feature key" description="Application feature controlled by this experiment." errorText={fieldErrors.featureKey} stretch>
										<Select
											selectedOption={getFeatureKeyOptions(form.featureKey).find((option) => option.value === form.featureKey) || null}
											options={getFeatureKeyOptions(form.featureKey)}
											placeholder="Select feature"
											onChange={({ detail }) => updateForm({ featureKey: detail.selectedOption.value })}
										/>
									</FormField>
								</Grid>
								<Grid gridDefinition={[{ colspan: 4 }, { colspan: 8 }]}>
									<FormField label="Experiment status" description="Controls whether the experiment is being prepared, scheduled, active, paused, or completed." stretch>
										<Select
											selectedOption={STATUS_OPTIONS.find((option) => option.value === form.status) || STATUS_OPTIONS[0]}
											options={STATUS_OPTIONS}
											onChange={({ detail }) => updateForm({ status: detail.selectedOption.value })}
										/>
									</FormField>
									<FormField
										label="Experiment period"
										description="Start and end date/time in your local timezone. Use Clear period inside the picker to reset the range."
										errorText={fieldErrors.startsAt || fieldErrors.endsAt}
									>
											<DateRangePicker
												value={form.startsDate && form.endsDate ? {
													type: "absolute",
													startDate: toPickerDateTime(form.startsDate, form.startsTime),
													endDate: toPickerDateTime(form.endsDate, form.endsTime),
												} : null}
												onChange={({ detail }) => {
													const start = splitPickerDateTime(detail.value?.startDate);
													const end = splitPickerDateTime(detail.value?.endDate);
													updateForm({ startsDate: start.date, startsTime: start.time, endsDate: end.date, endsTime: end.time });
												}}
												dateOnly={false}
												timeInputFormat="hh:mm"
												rangeSelectorMode="absolute-only"
												relativeOptions={[]}
												showClearButton
												isValidRange={(range) => {
													if (!range?.startDate) return { valid: false, errorMessage: "Select a start date and time." };
													if (!range.endDate) return { valid: false, errorMessage: "Select an end date and time." };
													if (new Date(range.startDate).getTime() >= new Date(range.endDate).getTime()) return { valid: false, errorMessage: "The end must be later than the start." };
													return { valid: true };
												}}
												i18nStrings={DATE_RANGE_I18N}
												placeholder="Select experiment start and end"
											/>
									</FormField>
								</Grid>
							</SpaceBetween>
						</Container>
									),
								},
								{
									id: "audience",
									label: "Audience",
									content: (

						<CohortConditionsEditor
							key={form.editorKey}
							conditions={form.cohortConditions}
							onChange={(cohortConditions) => updateForm({ cohortConditions })}
						/>
									),
								},
								{
									id: "variants",
									label: "Variants",
									content: (

						<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
							<Container header={<Header variant="h2" description="The baseline experience used for comparison.">Control ({100 - variantAllocation}%)</Header>}>
								<SpaceBetween size="m">
									<FormField label="Control name" description="Name displayed for the baseline group." errorText={fieldErrors.controlName}>
										<Input value={form.control.name} onChange={({ detail }) => updateGroup("control", { name: detail.value })} />
									</FormField>
									<FormField label="Control description" description="Optional explanation of the baseline behavior.">
										<Textarea rows={3} value={form.control.description} onChange={({ detail }) => updateGroup("control", { description: detail.value })} />
									</FormField>
									<FormField label="Control availability" description="Disabled groups do not receive new users.">
										<Toggle checked={form.control.enabled} onChange={({ detail }) => updateGroup("control", { enabled: detail.checked })}>Enabled</Toggle>
									</FormField>
									<FormField
										label="Control payload JSON"
										description="Feature configuration returned to Control users. Enter a JSON object or leave {} when no payload is required."
										errorText={getJsonObjectError(form.control.payloadText, "Control payload")}
										stretch
									>
										<Textarea rows={8} value={form.control.payloadText} onChange={({ detail }) => updateGroup("control", { payloadText: detail.value })} />
									</FormField>
								</SpaceBetween>
							</Container>
							<Container header={<Header variant="h2" description="The alternative experience being evaluated.">Variant ({variantAllocation}%)</Header>}>
								<SpaceBetween size="m">
									<FormField label="Variant name" description="Name displayed for the alternative group." errorText={fieldErrors.variantName}>
										<Input value={form.variant.name} onChange={({ detail }) => updateGroup("variant", { name: detail.value })} />
									</FormField>
									<FormField label="Variant description" description="Optional explanation of what changes for this group.">
										<Textarea rows={3} value={form.variant.description} onChange={({ detail }) => updateGroup("variant", { description: detail.value })} />
									</FormField>
									<FormField label="Variant availability" description="Disabled groups do not receive new users.">
										<Toggle checked={form.variant.enabled} onChange={({ detail }) => updateGroup("variant", { enabled: detail.checked })}>Enabled</Toggle>
									</FormField>
									<FormField label="Variant allocation, %" errorText={fieldErrors.allocationPercent} description={`Share of users assigned to Variant. Control automatically receives ${100 - variantAllocation}%.`}>
										<Grid gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}>
											<input
												type="range"
												min="0"
												max="100"
												step="1"
												value={variantAllocation}
												onChange={(event) => updateGroup("variant", { allocationPercent: Number(event.target.value) })}
												style={{ width: "100%", marginTop: "10px" }}
											/>
											<Input type="number" value={String(form.variant.allocationPercent)} onChange={({ detail }) => updateGroup("variant", { allocationPercent: detail.value })} />
										</Grid>
									</FormField>
									<FormField
										label="Variant payload JSON"
										description="Feature configuration returned to Variant users. Enter a JSON object or leave {} when no payload is required."
										errorText={getJsonObjectError(form.variant.payloadText, "Variant payload")}
										stretch
									>
										<Textarea rows={8} value={form.variant.payloadText} onChange={({ detail }) => updateGroup("variant", { payloadText: detail.value })} />
									</FormField>
								</SpaceBetween>
							</Container>
						</Grid>
									),
								},
								{
									id: "metrics",
									label: "Metrics",
									content: (
										<SpaceBetween size="m">
											<Container>
												<FormField label="Metric to add" description="Select what Control and Variant results should be compared by.">
													<SpaceBetween direction="horizontal" size="xs">
														<Select selectedOption={form.metricToAdd} options={availableMetricOptions} placeholder="Select metric" onChange={({ detail }) => updateForm({ metricToAdd: detail.selectedOption })} />
														<Button disabled={!form.metricToAdd} onClick={addMetric}>Add metric</Button>
													</SpaceBetween>
												</FormField>
											</Container>

						<Table
							trackBy="rowKey"
							wrapLines
							items={form.metrics}
							columnDefinitions={[
								{
									id: "metricKey",
									header: "Metric",
									cell: (metric) => metric.metricKey === RETENTION_METRIC_KEY ? (
										<SpaceBetween size="xxs">
											<Box variant="strong">{getRetentionMetricLabel(metric)}</Box>
											<Box variant="small" color="text-body-secondary">Activity: {getRetentionActivityLabel(metric.activityType) || "not selected"}</Box>
										</SpaceBetween>
									) : metric.metricKey === DEPOSIT_NUMBER_METRIC_KEY ? (
										<SpaceBetween size="xxs">
											<Box variant="strong">{getDepositNumberMetricLabel(metric)}</Box>
											<Box variant="small" color="text-body-secondary">Deposit order in the experiment</Box>
										</SpaceBetween>
									) : metric.metricKey,
									width: 220,
								},
								{
									id: "enabled",
									header: "Enabled",
									cell: (metric) => (
										<FormField label={`${metric.metricKey === RETENTION_METRIC_KEY ? getRetentionMetricLabel(metric) : metric.metricKey === DEPOSIT_NUMBER_METRIC_KEY ? getDepositNumberMetricLabel(metric) : metric.metricKey} tracking`}>
											<Toggle checked={metric.enabled} onChange={({ detail }) => updateMetric(metric.rowKey, { enabled: detail.checked })}>{metric.enabled ? "Enabled" : "Disabled"}</Toggle>
										</FormField>
									),
									width: 130,
								},
								{
									id: "settings",
									header: "Settings",
									cell: (metric) => {
										const metricIndex = form.metrics.findIndex((item) => item.rowKey === metric.rowKey);

										if (metric.metricKey === RETENTION_METRIC_KEY) {
											const selectedActivityOption = RETENTION_ACTIVITY_OPTIONS.find((option) => option.value === metric.activityType) || null;

											return (
												<Grid gridDefinition={[{ colspan: 7 }, { colspan: 5 }]}>
													<FormField
														label="Activity type"
														description="The user activity counted for retention."
														errorText={fieldErrors[`metric-${metricIndex}-activity`] || getRetentionActivityError(metric.activityType)}
													>
														<Select
															selectedOption={selectedActivityOption}
															options={RETENTION_ACTIVITY_OPTIONS}
															placeholder="Select activity type"
															onChange={({ detail }) => updateMetric(metric.rowKey, { activityType: detail.selectedOption.value })}
														/>
													</FormField>
													<FormField
														label="Retention day"
														description="A whole day number starting from 1, displayed as D1, D7, and so on."
														errorText={fieldErrors[`metric-${metricIndex}-day`] || getRetentionDayError(metric.retentionDay)}
													>
														<Input
															type="number"
															inputMode="numeric"
															step={1}
															placeholder="1"
															value={String(metric.retentionDay ?? "")}
															onChange={({ detail }) => updateMetric(metric.rowKey, { retentionDay: detail.value })}
														/>
													</FormField>
												</Grid>
											);
										}

										if (metric.metricKey === DEPOSIT_NUMBER_METRIC_KEY) {
											return (
												<FormField
													label="Deposit numbers"
													description="Whole deposit order numbers separated by commas. For example, 1, 2 will be displayed as DEP1 and DEP2."
													errorText={fieldErrors[`metric-${metricIndex}-deposit-numbers`] || parseDepositNumbers(metric.depositNumbersText).error}
												>
													<Input
														placeholder="1, 2"
														value={metric.depositNumbersText || ""}
														onChange={({ detail }) => updateMetric(metric.rowKey, { depositNumbersText: detail.value })}
													/>
												</FormField>
											);
										}

										return (
											<FormField
												label={`${metric.metricKey} settings`}
												description="Optional backend calculation parameters. Enter a JSON object or leave {} when no settings are required."
												errorText={getMetricSettingsError(metric)}
												stretch
											>
												<Textarea rows={4} value={metric.settingsText} onChange={({ detail }) => updateMetric(metric.rowKey, { settingsText: detail.value })} />
											</FormField>
										);
									},
								},
								{
									id: "actions",
									header: "Actions",
									cell: (metric) => (
										<Button onClick={() => setForm((current) => ({
											...current,
											metrics: current.metrics.filter((item) => item.rowKey !== metric.rowKey),
										}))}>Remove</Button>
									),
									width: 220,
								},
							]}
							header={<Header variant="h2" description="Each selected metric is calculated separately for Control and Variant.">Selected metrics ({form.metrics.length})</Header>}
							empty={<Box textAlign="center">No metrics selected</Box>}
						/>
										</SpaceBetween>
									),
								},
								{
									id: "json",
									label: "JSON",
									content: (
										<Container>
											<SpaceBetween size="m">
												<Box variant="p" color="text-body-secondary">
													Copy this A/B test as JSON to create it on another project: open Create A/B test there, paste the JSON and apply it.
												</Box>
												<SpaceBetween direction="horizontal" size="xs">
													<Button onClick={copyExperimentJson}>Copy JSON</Button>
													<Button variant="primary" onClick={applyExperimentJson}>Apply JSON</Button>
												</SpaceBetween>
												<FormField label="JSON" errorText={jsonError} stretch>
													<Textarea value={jsonText} onChange={({ detail }) => { setJsonText(detail.value); setJsonError(""); }} rows={24} />
												</FormField>
											</SpaceBetween>
										</Container>
									),
								},
							]}
						/>
					</SpaceBetween>
				)}
			</Modal>

			<Modal
				visible={deleteModal.visible}
				onDismiss={() => !deleteModal.loading && setDeleteModal({ visible: false, item: null, loading: false, error: "" })}
				header="Delete A/B test"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button disabled={deleteModal.loading} onClick={() => setDeleteModal({ visible: false, item: null, loading: false, error: "" })}>Cancel</Button>
							<Button variant="primary" loading={deleteModal.loading} onClick={confirmDelete}>Delete</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween size="m">
					{deleteModal.error ? <Alert type="error">{deleteModal.error}</Alert> : null}
					<Box>Delete draft experiment <b>{deleteModal.item?.name || deleteModal.item?.code}</b>? Deletion is only allowed when it has no assignments or metric events.</Box>
				</SpaceBetween>
			</Modal>

			<Modal
				visible={statisticsModal.visible}
				onDismiss={closeStatistics}
				header={`A/B test statistics${statisticsModal.item ? ` — ${statisticsModal.item.name}` : ""}`}
				size="max"
				footer={<Box float="right"><Button onClick={closeStatistics}>Close</Button></Box>}
			>
				<SpaceBetween size="m">
					<Grid gridDefinition={[{ colspan: 4 }, { colspan: 5 }, { colspan: 3 }]}>
						<FormField label="Breakdown">
							<SegmentedControl
								selectedId={statisticsFilter.groupBy}
								options={STATISTICS_GROUP_OPTIONS}
								onChange={({ detail }) => statisticsModal.item && loadStatistics(statisticsModal.item, { ...statisticsFilter, groupBy: detail.selectedId })}
							/>
						</FormField>
						<FormField label="Period (UTC)" description="Leave empty for the whole experiment.">
							<DateRangePicker
								value={statisticsFilter.range}
								onChange={({ detail }) => statisticsModal.item && loadStatistics(statisticsModal.item, { ...statisticsFilter, range: detail.value })}
								dateOnly
								rangeSelectorMode="absolute-only"
								relativeOptions={[]}
								showClearButton
								isValidRange={(range) => (range?.startDate && range?.endDate ? { valid: true } : { valid: false, errorMessage: "Select a start and end date." })}
								i18nStrings={DATE_RANGE_I18N}
								placeholder="Whole experiment"
							/>
						</FormField>
						<FormField label="Export">
							<Button iconName="download" loading={statisticsExporting} disabled={!statisticsModal.item} onClick={exportStatistics}>Export CSV by day</Button>
						</FormField>
					</Grid>
					{statisticsModal.loading && !statisticsModal.data ? (
						<Box textAlign="center" padding="xxl">Loading statistics…</Box>
					) : statisticsModal.error ? (
						<Alert type="error">{statisticsModal.error}</Alert>
					) : (
						<SpaceBetween size="m">
							<StatusIndicator type={statisticsModal.loading ? "loading" : statusType(statisticsModal.data?.status)}>{statisticsModal.loading ? "Updating…" : statisticsModal.data?.status || "unknown"}</StatusIndicator>
							<Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
								<Container header={<Header variant="h2">Control</Header>}>
									<SpaceBetween size="s">
										<Box variant="awsui-key-label">Users</Box>
										<Box fontSize="heading-l">{formatNumber(statisticsModal.data?.groups?.control?.usersCount)}</Box>
									</SpaceBetween>
								</Container>
								<Container header={<Header variant="h2">Variant</Header>}>
									<SpaceBetween size="s">
										<Box variant="awsui-key-label">Users</Box>
										<Box fontSize="heading-l">{formatNumber(statisticsModal.data?.groups?.variant?.usersCount)}</Box>
									</SpaceBetween>
								</Container>
							</Grid>
							{statisticsFilter.range && statisticsRows.some((metric) => metric.isRetention || /^DEP\d+$/.test(metric.metricKey)) ? (
								<Alert type="info">Retention and DEPn metrics are always calculated for the whole experiment.</Alert>
							) : null}
							<Table
								trackBy="rowKey"
								items={statisticsRows}
								columnDefinitions={[
									{
										id: "metricKey",
										header: "Metric",
										cell: (metric) => (
											<SpaceBetween size="xxs">
												<Box variant="strong">{metric.metricKey}</Box>
												{metric.activityType ? <Box variant="small" color="text-body-secondary">Activity: {getRetentionActivityLabel(metric.activityType) || metric.activityType}</Box> : null}
											</SpaceBetween>
										),
									},
									{ id: "control", header: "Control", cell: (metric) => formatStatisticsMetricValue(metric, metric.control) },
									{ id: "variant", header: "Variant", cell: (metric) => formatStatisticsMetricValue(metric, metric.variant) },
									{
										id: "difference",
										header: "Variant − Control",
										cell: (metric) => {
											const control = getStatisticsMetricNumber(metric, metric.control, true);
											const variant = getStatisticsMetricNumber(metric, metric.variant, true);
											if (control === null || variant === null) return "-";
											const difference = variant - control;
											const percent = !metric.isRetention && control ? ` (${difference >= 0 ? "+" : ""}${((difference / control) * 100).toFixed(2)}%)` : "";

											return `${difference >= 0 ? "+" : ""}${formatStatisticsMetricNumber(metric, difference)}${percent}`;
										},
									},
									{ id: "winner", header: "Winner", cell: (metric) => <StatusIndicator type={winnerType(metric.winner)}>{metric.winner || "Not determined"}</StatusIndicator> },
								]}
								header={<Header variant="h2">{statisticsFilter.range ? "Metric comparison for the period" : "Metric comparison"}</Header>}
								empty={<Box textAlign="center">No metric statistics available</Box>}
							/>
							{statisticsModal.data?.breakdown ? (
								<Table
									trackBy="rowKey"
									items={breakdownItems}
									wrapLines={false}
									columnDefinitions={[
										{ id: "metric", header: "Metric", cell: (item) => <Box variant="strong">{item.metricKey}</Box> },
										{ id: "group", header: "Group", cell: (item) => GROUP_LABELS[item.groupKey] },
										...[...(statisticsModal.data.breakdown.rows || [])].reverse().map((row) => ({
											id: row.period,
											header: formatStatisticsPeriod(row, statisticsModal.data.breakdown.groupBy),
											cell: (item) => formatMetricValue(item.metricKey, row[item.groupKey]?.values?.[item.metricKey]),
										})),
									]}
									header={<Header variant="h2">{statisticsModal.data.breakdown.groupBy === "week" ? "By week (UTC)" : "By day (UTC)"}</Header>}
									empty={<Box textAlign="center">No data for the selected period</Box>}
								/>
							) : null}
						</SpaceBetween>
					)}
				</SpaceBetween>
			</Modal>
		</SpaceBetween>
	);
}

export default ABTests;
